<template>
  <div class="toast-container">
    <div 
      class="toast" 
      role="alert" 
      aria-live="assertive" 
      aria-atomic="true"
      :class="notificationClass"
    >
      <div class="toast-header">
        <i :class="notificationIcon + ' me-2'"></i>
        <strong class="me-auto">{{ notificationTitle }}</strong>
        <small>{{ timeText }}</small>
        <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      <div class="toast-body">
        {{ notification?.message }}
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Toast } from 'bootstrap';

export default {
  name: 'Notifications',
  setup() {
    const store = useStore();
    const notification = computed(() => store.state.notification);
    const timeText = ref('just now');

    // Notification type classes
    const notificationClass = computed(() => {
      if (!notification.value) return '';
      
      const typeMap = {
        'success': 'text-bg-success',
        'danger': 'text-bg-danger',
        'error': 'text-bg-danger',
        'warning': 'text-bg-warning',
        'info': 'text-bg-info'
      };
      
      return typeMap[notification.value.type] || 'text-bg-info';
    });

    // Notification icon based on type
    const notificationIcon = computed(() => {
      if (!notification.value) return 'ph-duotone ph-info';
      
      switch(notification.value.type) {
        case 'success':
          return 'ph-duotone ph-check-circle text-success';
        case 'danger':
        case 'error':
          return 'ph-duotone ph-x-circle text-danger';
        case 'warning':
          return 'ph-duotone ph-warning-circle text-warning';
        case 'info':
        default:
          return 'ph-duotone ph-info text-info';
      }
    });

    // Notification title based on type
    const notificationTitle = computed(() => {
      if (!notification.value) return 'Notification';
      
      switch(notification.value.type) {
        case 'success':
          return 'Success';
        case 'danger':
        case 'error':
          return 'Error';
        case 'warning':
          return 'Warning';
        case 'info':
        default:
          return 'Information';
      }
    });

    // Show notification when it changes
    watch(notification, async (newVal) => {
      if (newVal?.message) {
        timeText.value = 'just now';
        
        // Wait for the DOM to update with new notification content
        await nextTick(); 

        const toastEl = document.querySelector('.toast'); 
        if (toastEl) {
           
            try {
                const currentToastInstance = Toast.getOrCreateInstance(toastEl, { 
                    delay: 5000, 
                    autohide: true 
                });
                currentToastInstance.show();
             
            } catch (error) {
                console.error('Error showing Bootstrap toast:', error);
            }
        } else {
            console.error('Could not find .toast element to show notification.');
        }
      }
    });

    return {
      notification,
      notificationClass,
      notificationIcon,
      notificationTitle,
      timeText
    };
  }
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1090;
}

.toast {
  width: 350px;
  max-width: 100%;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.15);
}
</style> 